import { useState, useEffect, useCallback, useMemo } from 'react';
import { getFileBoxType, isDeleteAtPassed } from './utils';

// Determine the file type: image / video / audio / dcm / other
export function useFileType(file) {
  return useMemo(() => getFileBoxType(file), [file]);
}

// Live countdown to a file's `deleteAt` timestamp. Re-renders once per second
// while counting down; stops ticking (and reports expired: true) once the
// time has passed, so callers don't need their own timer/cleanup.
export function useCountdown(deleteAt) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!deleteAt || isDeleteAtPassed({ deleteAt }, now)) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [deleteAt]);

  if (!deleteAt) return { remainingMs: null, expired: false };

  const remainingMs = Math.max(0, deleteAt - now);
  return { remainingMs, expired: remainingMs <= 0 };
}

// Track the file's upload progress
// (file.onUploadProgress is an array of callbacks invoked by the outer upload logic)
export function useUploadProgress(file) {
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!file) return;
    const handler = () => forceRender((n) => n + 1);
    if (!file.onUploadProgress) file.onUploadProgress = [];
    file.onUploadProgress.push(handler);
    return () => {
      file.onUploadProgress = file.onUploadProgress?.filter((fn) => fn !== handler);
    };
  }, [file]);

  return file?.uploadState || {};
}

// Handle downloading the file
export function useDownload() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(
    (e, file) => {
      e?.preventDefault();
      if (downloading) return;
      setDownloading(true);

      const finishDownload = (url) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = file?.name || file?.originalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setDownloading(false);
      };

      if (file.uri) {
        fetch(file.uri)
          .then((res) => res.blob())
          .then((blob) => finishDownload(window.URL.createObjectURL(blob)))
          .catch((error) => {
            console.error('Error downloading file:', error);
            setDownloading(false);
          });
      } else {
        finishDownload(window.URL.createObjectURL(file));
      }
    },
    [downloading]
  );

  return { downloading, handleDownload };
}
